/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import styles from './styles.module.scss';
import { Notification, View } from '@az/starc-ui';
import { ExclamationTriangle } from '@az/starc-ui-icons';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import usePrevious from '@/utils/usePrevious';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { type ItemAvailability } from '@/features/orders';
import { type TSkuAvailability } from '@/features/product';

export type Props = {
  selectedFulfillmentID: number;
  availableQuantity: number | undefined;
  selectedQuantity: number;
  nddQuantity: number | undefined;
  sddQuantity: number | undefined;
  skuAvailabilityInfo?: ItemAvailability | TSkuAvailability | undefined;
};
const labelMap = {
  label_max_quantity_sdd: 'label_max_quantity_sdd',
  label_max_quantity_ndd: 'label_max_quantity_ndd',
  label_max_quantity_hd: 'label_max_quantity_hd',
  label_quantity_exceeded_ndd: 'label_quantity_exceeded_ndd',
  label_max_quantity_bopus: 'label_max_quantity_bopus',
  label_deliverymethod_unavailable: 'label_deliverymethod_unavailable',
  label_cart_lineItem_MaximumQuantityReached: 'label_cart_lineItem_MaximumQuantityReached',
};

export function MaxQuantityNotificationBlockPdpV2(props: Props) {
  const labels = useLabels(labelMap);
  const { data: storeDetails } = useStoreDetailsData();
  const previousStoreNumber = usePrevious(storeDetails?.storeNumber);
  const {
    selectedFulfillmentID,
    nddQuantity = 0,
    sddQuantity = 0,
    selectedQuantity,
    availableQuantity = 0,

    skuAvailabilityInfo,
  } = props;
  const locale = useLocale();

  const checkAvailability = () => {
    if (availableQuantity > selectedQuantity) {
      return true;
    }
    return false;
  };
  if (!skuAvailabilityInfo) {
    return null;
  }
  let message = '';
  if (selectedFulfillmentID == FULFILLMENT_METHODS.SAMEDAY) {
    if (
      storeDetails?.storeNumber !== previousStoreNumber &&
      sddQuantity != null &&
      sddQuantity <= 0
    ) {
      message = labels.label_deliverymethod_unavailable;
    } else if (!checkAvailability()) {
      message = labels.label_max_quantity_sdd;
    }
  } else if (selectedFulfillmentID == FULFILLMENT_METHODS.NEXTDAY) {
    if (availableQuantity <= selectedQuantity) {
      message = labels.label_max_quantity_ndd;
    }
  } else if (selectedFulfillmentID == FULFILLMENT_METHODS.ONLINEORDER) {
    if (
      nddQuantity &&
      nddQuantity > 0 &&
      nddQuantity < selectedQuantity &&
      availableQuantity > selectedQuantity
    ) {
      message = labels.label_quantity_exceeded_ndd;
    } else if (availableQuantity <= selectedQuantity) {
      message = labels.label_max_quantity_hd;
    }
  } else if (selectedFulfillmentID == FULFILLMENT_METHODS.STOREORDER) {
    if (!checkAvailability()) {
      message = labels.label_max_quantity_bopus;
    }
  }

  if (locale === countryCodes.mx && !checkAvailability()) {
    message = labels.label_cart_lineItem_MaximumQuantityReached;
  }

  // Not needed for PVP V2.
  // This logic was carried over from the old max quantity notification block,
  // which handled both PDP and cart notifications using `messageIsNotification`.
  // In this context, `message` is never treated as a notification, making this unnecessary.

  if (message) {
    return (
      <View className={styles.maxQuantityNotification}>
        <Notification
          type="custom"
          customNotification={{
            svg: ExclamationTriangle,
            iconColor: 'yellow-100',
            barColor: 'yellow-100',
            bgColor: 'white',
          }}
          text={message}
        />
      </View>
    );
  }
  return null;
}
